import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CartContext from '../../../contexts/CartContext';
import { useCustomer } from '../../../contexts/CustomerContext';
import CartUtils from '../../../utils/CartUtils';

// Server reads [points].max_redeem_cents_per_order from config.toml and
// clamps for real. This mirrors the default in the Rust config — if the
// real server cap is lower, the server still wins; we just risk asking
// for slightly more than it'll honor. Worst case the customer sees the
// returned `points_redeemed` reflect the lower amount.
const FALLBACK_MAX_REDEEM_CENTS = 5000;

const PointsRedemptionPanel: React.FC = () => {
  const { cart, setCartState } = useContext(CartContext);
  const { points } = useCustomer();
  const [draft, setDraft] = useState('');
  const [preTaxCents, setPreTaxCents] = useState<number | null>(null);

  // Recompute the pre-tax (subtotal + shipping − discount code) so the
  // ceiling for the input reflects the actual order. CartUtils.getShipping
  // is async and may briefly be undefined; we render the cap conservatively
  // until it resolves.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const utils = new CartUtils({ ...cart, pointsToUse: 0 });
      const items = Object.values(cart.items);
      const subtotal = items.reduce(
        (s, i) => s + i.count * (i.item.price ?? 0),
        0,
      );
      const discount = cart.discountAmount ?? 0;
      const shipping = (await utils.getShipping()) ?? 0;
      if (!cancelled) {
        setPreTaxCents(Math.max(0, subtotal - discount + shipping));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cart]);

  const available = points?.available ?? 0;

  // Cap = min(balance, per-order cap, pre-tax − 1). The −1 keeps at least
  // a cent on the order so the payment processor still sees a real charge.
  const maxRedeemable = useMemo(() => {
    if (preTaxCents === null) return 0;
    const byOrder = Math.max(0, preTaxCents - 1);
    const upper = Math.min(available, FALLBACK_MAX_REDEEM_CENTS, byOrder);
    return Math.max(0, upper);
  }, [available, preTaxCents]);

  if (!points || available <= 0 || maxRedeemable <= 0) {
    return null;
  }

  const applied = cart.pointsToUse ?? 0;

  const onApply = () => {
    const parsed = parseInt(draft, 10);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setCartState({ ...cart, pointsToUse: undefined });
      setDraft('');
      return;
    }
    const clamped = Math.min(parsed, maxRedeemable);
    setCartState({ ...cart, pointsToUse: clamped });
    setDraft(String(clamped));
  };

  const onUseMax = () => {
    setCartState({ ...cart, pointsToUse: maxRedeemable });
    setDraft(String(maxRedeemable));
  };

  const onRemove = () => {
    setCartState({ ...cart, pointsToUse: undefined });
    setDraft('');
  };

  return (
    <Card sx={{ mb: 2 }} className="points-redemption">
      <CardContent>
        <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Use points
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {available.toLocaleString()} pts available ($
            {(available / 100).toFixed(2)})
          </Typography>
        </Stack>

        {applied > 0 ? (
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Applied: {applied.toLocaleString()} pts (−$
                {(applied / 100).toFixed(2)})
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Subtracted from your order pre-tax.
              </Typography>
            </Box>
            <Button onClick={onRemove} size="small" sx={{ textTransform: 'none' }}>
              Remove
            </Button>
          </Stack>
        ) : (
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              type="number"
              size="small"
              placeholder={`Up to ${maxRedeemable}`}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              inputProps={{ min: 0, max: maxRedeemable, step: 1 }}
              sx={{ maxWidth: 160 }}
            />
            <Button
              onClick={onApply}
              variant="contained"
              size="small"
              sx={{ textTransform: 'none' }}
              disabled={draft.trim() === ''}
            >
              Apply
            </Button>
            <Button
              onClick={onUseMax}
              size="small"
              sx={{ textTransform: 'none' }}
            >
              Use max ({maxRedeemable.toLocaleString()} pts)
            </Button>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default PointsRedemptionPanel;
